import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { Scene, Shot } from '../types/database';

const SCENES_KEY = 'scenes';

// Fetch scenes for a project
export function useScenes(projectId: string | null) {
  return useQuery({
    queryKey: [SCENES_KEY, projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await supabase
        .from('scenes')
        .select(`
          *,
          shots (*)
        `)
        .eq('project_id', projectId)
        .order('order_index')
        .order('order_index', { referencedTable: 'shots' });

      if (error) throw error;
      return data || [];
    },
    enabled: !!projectId,
  });
}

// Create a new scene
export function useCreateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      name,
      orderIndex,
    }: {
      projectId: string;
      name: string;
      orderIndex: number;
    }) => {
      const { data, error } = await supabase
        .from('scenes')
        .insert({
          project_id: projectId,
          name,
          order_index: orderIndex,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Scene;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Update a scene
export function useUpdateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sceneId,
      projectId,
      updates,
    }: {
      sceneId: string;
      projectId: string;
      updates: Partial<Scene>;
    }) => {
      const { data, error } = await supabase
        .from('scenes')
        .update(updates)
        .eq('id', sceneId)
        .select()
        .single();

      if (error) throw error;
      return data as Scene;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Delete a scene
export function useDeleteScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sceneId,
      projectId,
    }: {
      sceneId: string;
      projectId: string;
    }) => {
      const { error } = await supabase.from('scenes').delete().eq('id', sceneId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Reorder scenes
export function useReorderScenes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      sceneIds,
    }: {
      projectId: string;
      sceneIds: string[];
    }) => {
      // Update order_index for each scene
      const updates = sceneIds.map((id, index) => ({
        id,
        order_index: index,
      }));

      const { error } = await supabase.from('scenes').upsert(updates);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Create a new shot
export function useCreateShot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sceneId,
      projectId,
      name,
      orderIndex,
      durationSec = 5,
    }: {
      sceneId: string;
      projectId: string;
      name: string;
      orderIndex: number;
      durationSec?: number;
    }) => {
      const { data, error } = await supabase
        .from('shots')
        .insert({
          scene_id: sceneId,
          name,
          order_index: orderIndex,
          duration_sec: durationSec,
          visual_type: 'image',
        })
        .select()
        .single();

      if (error) throw error;
      return data as Shot;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Update a shot
export function useUpdateShot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shotId,
      projectId,
      updates,
    }: {
      shotId: string;
      projectId: string;
      updates: Partial<Shot>;
    }) => {
      const { data, error } = await supabase
        .from('shots')
        .update(updates)
        .eq('id', shotId)
        .select()
        .single();

      if (error) throw error;
      return data as Shot;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Delete a shot
export function useDeleteShot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shotId,
      projectId,
    }: {
      shotId: string;
      projectId: string;
    }) => {
      const { error } = await supabase.from('shots').delete().eq('id', shotId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}

// Reorder shots
export function useReorderShots() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sceneId,
      projectId,
      shotIds,
    }: {
      sceneId: string;
      projectId: string;
      shotIds: string[];
    }) => {
      // Update order_index for each shot
      const updates = shotIds.map((id, index) => ({
        id,
        order_index: index,
      }));

      const { error } = await supabase.from('shots').upsert(updates);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [SCENES_KEY, variables.projectId] });
    },
  });
}
